import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Loader2, Plus, Search, UserPlus, Pencil, Trash2, RotateCcw, Power } from "lucide-react";
import { toast } from "sonner";

export default function SubBrokerList() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/subbroker/list?search=${encodeURIComponent(search)}`);
      setRows(data.subbrokers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sub brokers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete sub broker "${name}"? Their login + mappings will be disabled.`)) return;
    try {
      await api.delete(`/subbroker/${id}`);
      toast.success("Sub broker deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleResetCredentials = async (id, name) => {
    if (!window.confirm(`Reset "${name}" back to default password (kinntegra123) and PIN (1234)? They will be forced to change both on next login.`)) return;
    try {
      const { data } = await api.post(`/auth/admin-reset-credentials/${id}`);
      toast.success(`Reset done — password=${data.default_password}, PIN=${data.default_pin}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const handleToggleStatus = async (id, name, currentStatus) => {
    const next = currentStatus === "active" ? "inactive" : "active";
    const verb = next === "inactive" ? "Deactivate" : "Re-activate";
    if (!window.confirm(`${verb} "${name}"? ${next === "inactive" ? "They will no longer be able to log in until you re-activate them." : "They will be able to log in again."}`)) return;
    try {
      await api.put(`/subbroker/${id}`, { status: next });
      toast.success(`${name} ${next === "inactive" ? "deactivated" : "re-activated"}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="subbroker-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Sub Brokers</h1>
          <p className="text-sm text-gray-500">Manage your distributor network · {rows.length} active</p>
        </div>
        <Link to="/sub-brokers/create">
          <Button data-testid="subbroker-create-btn" className="gap-2" style={{ backgroundColor: "#1a3f3a" }}>
            <Plus className="w-4 h-4" /> Create Sub Broker
          </Button>
        </Link>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input
              placeholder="Search by code, name, email, PAN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchData()}
              className="pl-9"
              data-testid="subbroker-search"
            />
          </div>
          <Button variant="outline" onClick={fetchData}>Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="py-2 px-2">Code</th>
                  <th className="py-2 px-2">Name</th>
                  <th className="py-2 px-2">Email</th>
                  <th className="py-2 px-2">Mobile</th>
                  <th className="py-2 px-2">PAN</th>
                  <th className="py-2 px-2">ARN</th>
                  <th className="py-2 px-2">Mapped Clients</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-gray-400 py-12">
                    <UserPlus className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    No sub brokers yet. Click <b>Create Sub Broker</b> to add one.
                  </td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} data-testid={`subbroker-row-${r.sub_broker_code}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-2 font-mono text-xs">{r.sub_broker_code}</td>
                    <td className="py-2 px-2 font-medium">{r.sub_broker_name}</td>
                    <td className="py-2 px-2 text-gray-600">{r.email}</td>
                    <td className="py-2 px-2 font-mono text-xs">{r.mobile}</td>
                    <td className="py-2 px-2 font-mono text-xs">{r.pan || "—"}</td>
                    <td className="py-2 px-2 font-mono text-xs">{r.arn_code || "—"}</td>
                    <td className="py-2 px-2">
                      <Link to={`/sub-brokers/client-mapping?sb=${r.id}`} className="text-teal-600 hover:underline font-medium">
                        {r.mapped_clients || 0}
                      </Link>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Link to={`/sub-brokers/edit/${r.id}`}>
                          <Button size="sm" variant="ghost" data-testid={`edit-${r.sub_broker_code}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => handleResetCredentials(r.id, r.sub_broker_name)} data-testid={`reset-${r.sub_broker_code}`} title="Reset password + PIN to defaults">
                          <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleStatus(r.id, r.sub_broker_name, r.status)}
                          data-testid={`toggle-${r.sub_broker_code}`}
                          title={r.status === "active" ? "Deactivate (block login)" : "Re-activate"}
                        >
                          <Power className={`w-3.5 h-3.5 ${r.status === "active" ? "text-gray-500" : "text-emerald-600"}`} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id, r.sub_broker_name)} data-testid={`delete-${r.sub_broker_code}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
